/**
 * Filters step — header selectors (Category, Number, Date).
 *
 * A selector has no grid position and no live preview of its own the way a
 * widget's config form has: it produces no data by itself, only a WHERE
 * clause once an end user has picked something (see `selectorRuntime.ts`).
 * What this step configures is exactly what `DashboardSelectorBar` needs to
 * render the control and what field it narrows — everything else is decided
 * at view time, in the header itself.
 */

import { useState } from 'react';
import { CalendarDays, ChevronDown, ChevronUp, Hash, List, Trash2 } from 'lucide-react';
import { createId } from '../services/storage';
import { numericFields, useSourceFields } from './components/useSourceFields';
import type { DashboardConfig, DashboardSelector, SelectorType } from './types/dashboardTypes';

const inputClass = 'gl-input px-2.5 py-1.5 text-xs';

const TYPE_META: Record<SelectorType, { label: string; icon: typeof List; hint: string }> = {
  category: {
    label: 'Category',
    icon: List,
    hint: 'A dropdown of a field’s own distinct values — a district, an equipment type, a status.',
  },
  number: {
    label: 'Number',
    icon: Hash,
    hint: 'A dropdown of a numeric field’s values, alone or as a From/To range.',
  },
  date: {
    label: 'Date',
    icon: CalendarDays,
    hint: 'A calendar — one day, or a From/To range.',
  },
};

interface Props {
  config: DashboardConfig;
  onChange: (config: DashboardConfig) => void;
}

export default function DashboardFilters({ config, onChange }: Props) {
  const addSelector = (type: SelectorType) => {
    const selector: DashboardSelector = {
      id: createId('sel'),
      type,
      label: TYPE_META[type].label,
      dataSourceId: config.dataSources[0]?.id ?? '',
      field: '',
      mode: 'single',
    };
    onChange({ ...config, selectors: [...config.selectors, selector] });
  };

  const updateSelector = (id: string, changes: Partial<DashboardSelector>) =>
    onChange({
      ...config,
      selectors: config.selectors.map((selector) =>
        selector.id === id ? { ...selector, ...changes } : selector,
      ),
    });

  const removeSelector = (id: string) =>
    onChange({ ...config, selectors: config.selectors.filter((selector) => selector.id !== id) });

  const moveSelector = (id: string, direction: -1 | 1) => {
    const index = config.selectors.findIndex((selector) => selector.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= config.selectors.length) return;
    const next = [...config.selectors];
    [next[index], next[target]] = [next[target], next[index]];
    onChange({ ...config, selectors: next });
  };

  return (
    <div className="max-w-2xl space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Filters ({config.selectors.length})</h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Header controls an end user picks from — a category, a number, a date. Choosing a value
          automatically narrows every widget built on the same data source; nothing to wire per widget.
        </p>
      </div>

      {config.dataSources.length === 0 ? (
        <div className="gl-panel border-dashed py-10 text-center">
          <p className="text-sm text-slate-500">Add a data source first</p>
          <p className="text-xs text-slate-400">A selector always filters one data source's own field.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(TYPE_META) as SelectorType[]).map((type) => {
              const Icon = TYPE_META[type].icon;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => addSelector(type)}
                  className="gl-btn gl-btn--secondary px-2.5 py-1.5 text-xs"
                >
                  <Icon className="h-3.5 w-3.5" />
                  Add {TYPE_META[type].label}
                </button>
              );
            })}
          </div>

          {config.selectors.length === 0 ? (
            <div className="gl-panel border-dashed py-10 text-center">
              <p className="text-sm text-slate-500">No filters yet</p>
              <p className="text-xs text-slate-400">Add a Category, Number or Date selector to get started.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {config.selectors.map((selector, index) => (
                <SelectorCard
                  key={selector.id}
                  selector={selector}
                  dataSources={config.dataSources}
                  onChange={(changes) => updateSelector(selector.id, changes)}
                  onRemove={() => removeSelector(selector.id)}
                  onMoveUp={index > 0 ? () => moveSelector(selector.id, -1) : undefined}
                  onMoveDown={index < config.selectors.length - 1 ? () => moveSelector(selector.id, 1) : undefined}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function SelectorCard({
  selector,
  dataSources,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  selector: DashboardSelector;
  dataSources: DashboardConfig['dataSources'];
  onChange: (changes: Partial<DashboardSelector>) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const source = dataSources.find((entry) => entry.id === selector.dataSourceId);
  const { fields, loading } = useSourceFields(source);
  // Category and Date both offer every field, not just ones already inferred
  // as that type: type inference reads a 500-row sample of raw values (see
  // `inferType` in localSource.ts) and can miss a date column whose format it
  // doesn't recognise, or a category field the sample happened to see as
  // numbers-only. Letting the admin pick any field sidesteps that guess
  // entirely — Number stays restricted, since a non-numeric field genuinely
  // has no min/max/range for a Number control to offer.
  const fieldOptions = selector.type === 'number' ? numericFields(fields) : fields;
  const Icon = TYPE_META[selector.type].icon;

  return (
    <li className="gl-panel space-y-3 p-3">
      <div className="flex items-start gap-2">
        <span className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
          <Icon className="h-3.5 w-3.5 text-slate-500" />
        </span>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] font-medium text-slate-500">Label</span>
              <input
                value={selector.label}
                onChange={(event) => onChange({ label: event.target.value })}
                className={`mt-0.5 w-full ${inputClass}`}
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-500">Data source</span>
              <select
                value={selector.dataSourceId}
                onChange={(event) => onChange({ dataSourceId: event.target.value, field: '' })}
                className={`mt-0.5 w-full ${inputClass}`}
              >
                {dataSources.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-[11px] font-medium text-slate-500">Field</span>
            <select
              value={selector.field}
              onChange={(event) => onChange({ field: event.target.value })}
              className={`mt-0.5 w-full ${inputClass}`}
            >
              <option value="">
                {loading ? 'Loading fields…' : fieldOptions.length === 0 ? 'No matching fields' : 'Select a field…'}
              </option>
              {fieldOptions.map((field) => (
                <option key={field.name} value={field.name}>
                  {field.alias}
                </option>
              ))}
            </select>
            {selector.type !== 'category' && fieldOptions.length === 0 && !loading && (
              <span className="mt-1 block text-[10.5px] text-amber-600">
                This layer reports no {selector.type} fields.
              </span>
            )}
          </label>

          {selector.type === 'category' ? (
            <label className="flex items-center gap-1.5 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={Boolean(selector.multiple)}
                onChange={(event) => onChange({ multiple: event.target.checked })}
                className="h-3.5 w-3.5 accent-blue-600"
              />
              Allow more than one value at once
            </label>
          ) : (
            <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 text-xs">
              {(['single', 'range'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={(selector.mode ?? 'single') === mode}
                  onClick={() => onChange({ mode })}
                  className={`rounded-md px-2 py-1.5 font-medium transition ${
                    (selector.mode ?? 'single') === mode
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {mode === 'single' ? 'Single value' : 'Range'}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-shrink-0 flex-col items-center gap-0.5">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!onMoveUp}
            aria-label="Move up"
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!onMoveDown}
            aria-label="Move down"
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          {confirmingRemove ? (
            <button
              type="button"
              onClick={onRemove}
              className="mt-1 rounded px-1.5 py-1 text-[10px] font-semibold text-red-600 hover:bg-red-50"
            >
              Confirm
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingRemove(true)}
              onBlur={() => setConfirmingRemove(false)}
              aria-label="Remove"
              className="mt-1 rounded p-1 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
